import {Box, Divider, Grid, Typography} from '@mui/material';
import {FilterType, SortTypeEnum} from 'api/common';
import {pascalCase} from 'change-case';
import {AppButton} from 'components/buttons';
import {AddBoardItemComment, BoardItemComment} from 'components/comments';
import {AppInput, AppSelect} from 'components/form';
import {SelectOptionsType} from 'global';
import {useDebounceEffect, useGetLookUps} from 'hooks';
import {useGetActivitiesByObjectTypeAndObjectIdAPI} from 'hooks/activities/useActivityApi';
import {ActivityModel, LookupCategoryDescriptions, LookupCategoryTypes} from 'models';
import {ItemDataResponse} from 'models/board';
import React, {useCallback, useEffect, useState} from 'react';

interface CommentsBodyProps {
    itemData: ItemDataResponse;
}

export const CommentsBody: React.FC<CommentsBodyProps> = ({itemData}) => {
    const [activities, setActivities] = useState<ActivityModel[]>([]);
    const [commentSearchValue, setCommentSearchValue] = useState<string | undefined>();
    const [currentCommentStatus, setCurrentCommentStatus] = useState<string>('Active');
    const [commentStatusOptions, setCommentStatusOptions] = useState<SelectOptionsType[]>([]);
    const {
        acitivtyByObjectIdAndObjectTypeResponse: commentsResponse,
        activityByObjectIdAndObjectType: fetchComments,
        activityByObjectIdAndObjectTypeLoading: commmentLoading,
    } = useGetActivitiesByObjectTypeAndObjectIdAPI();
    const {getLookups, getLookupsLoading, getLookupsResponse} = useGetLookUps();
    useEffect(() => {
        getLookups({
            variables: {
                where: {
                    type: {
                        eq: LookupCategoryDescriptions[LookupCategoryTypes.CommentType],
                    },
                },
                order: [
                    {
                        title: SortTypeEnum.ASC,
                    },
                ],
            },
        });
    }, []);

    useEffect(() => {
        if (!getLookupsLoading && getLookupsResponse) {
            const statuses = getLookupsResponse.nodes.map(item => ({
                label: pascalCase(item.title),
                value: item.title,
            })) as SelectOptionsType[];
            setCommentStatusOptions(statuses);
        }
    }, [getLookupsLoading, getLookupsResponse]);
    const generateWhereCondition = useCallback((): FilterType<ActivityModel> | undefined => {
        const andFilters: any[] = [];

        if (currentCommentStatus === 'Active' || currentCommentStatus === 'Updated') {
            andFilters.push({status: {contains: '{"Active":true'}});
        } else if (currentCommentStatus === 'Deleted') {
            andFilters.push({status: {contains: '{"Active":false'}});
        }

        // Add OR group for search filters
        if (commentSearchValue) {
            andFilters.push({
                or: [
                    {data: {contains: commentSearchValue}},
                    {user: {contains: `"firstName":"${commentSearchValue}`}},
                    {user: {contains: `"lastName":"${commentSearchValue}`}},
                ],
            });
        }

        return andFilters.length > 0 ? {and: andFilters} : undefined;
    }, [commentSearchValue, currentCommentStatus]);

    const commentFetchByLimit = (limit: number) => {
        fetchComments({
            variables: {
                objectId: itemData.id,
                objectType: 'ItemData',
                first: limit,
                where: generateWhereCondition(),
                order: [
                    {
                        activityDate: SortTypeEnum.DESC,
                    },
                ],
            },
        });
    };

    const appendCommentsUnique = useCallback((newComments: ActivityModel[]) => {
        setActivities(prev => {
            if (prev) {
                const ids = prev.map(item => item.id);
                const newItems = newComments.filter(item => !ids.includes(item.id));
                return [...prev, ...newItems];
            }
            return newComments;
        });
    }, []);

    const handleRemoveActivity = (id: string) => {
        setActivities([]);
        setTimeout(() => {
            if (itemData && commentsResponse) {
                fetchComments({
                    variables: {
                        objectId: itemData.id,
                        objectType: 'ItemData',
                        first: 3,
                        where: generateWhereCondition(),
                        order: [
                            {
                                activityDate: SortTypeEnum.DESC,
                            },
                        ],
                    },
                });
            }
        }, 1000);
    };

    const handleUpdateActivity = (activity: ActivityModel) => {
        setActivities(prev => prev.map(item => (item.id === activity.id ? activity : item)));
    };

    const handleActivityAdd = (activity: ActivityModel) => {
        setActivities(prev => [activity, ...prev]);
    };

    const handleNext = () => {
        if (itemData && commentsResponse) {
            fetchComments({
                variables: {
                    objectId: itemData.id,
                    objectType: 'ItemData',
                    first: 3,
                    after: commentsResponse.pageInfo.endCursor,
                    where: generateWhereCondition(),
                    order: [
                        {
                            activityDate: SortTypeEnum.DESC,
                        },
                    ],
                },
            });
        }
    };

    useEffect(() => {
        if (itemData) {
            commentFetchByLimit(3);
        }
    }, []);

    useEffect(() => {
        if (itemData) {
            setActivities([]);
            commentFetchByLimit(3);
        }
    }, [currentCommentStatus]);

    useDebounceEffect(
        () => {
            setActivities([]);
            if (itemData) {
                commentFetchByLimit(3);
            }
        },
        [commentSearchValue],
        1000,
    );

    useEffect(() => {
        if (!commmentLoading && commentsResponse && commentsResponse) {
            appendCommentsUnique(commentsResponse.nodes);
        }
    }, [commentsResponse, commmentLoading]);

    return (
        <Grid container spacing={1}>
            <Grid item xs={12} id="Comments" pt={2}>
                <Box display="flex" alignItems="end">
                    <Typography variant="h5">Comments</Typography>
                </Box>
                <Divider
                    sx={{
                        my: 1,
                        borderStyle: 'dotted',
                        borderColor: 'tertiary.main',
                        borderWidth: '1px',
                    }}
                />
            </Grid>

            <Grid item xs={12} width="100%" sx={{overflowX: 'auto'}}>
                {itemData && <AddBoardItemComment addComment={handleActivityAdd} selectedItem={itemData} />}
            </Grid>

            <Grid item xs={12}>
                <Divider
                    sx={{
                        my: 1,
                        borderStyle: 'dotted',
                        borderColor: 'tertiary.main',
                        borderWidth: '1px',
                    }}
                />
                <Box display={'flex'} alignItems="center">
                    <AppInput
                        value={commentSearchValue}
                        onChange={event => setCommentSearchValue(event.target.value)}
                        placeholder="Search Comments"
                        sx={{maxWidth: '200px'}}
                    />
                    <AppSelect
                        sx={{ml: 1, minWidth: '120px'}}
                        options={commentStatusOptions}
                        value={currentCommentStatus}
                        onChange={event => setCurrentCommentStatus(event.target.value as string)}
                        placeholder="Select Status"
                    />
                </Box>
            </Grid>

            {activities && activities.length > 0 ? (
                activities.map((comment: ActivityModel) => (
                    <BoardItemComment
                        currentCommentStatus={currentCommentStatus}
                        boardId={itemData.itemStatus?.boardId}
                        removeActivity={handleRemoveActivity}
                        editCommentHandler={handleUpdateActivity}
                        comment={comment}
                        key={comment.id}
                    />
                ))
            ) : (
                <Grid item xs={12}>
                    <Typography variant="body2" color="textSecondary">
                        No comments found!!
                    </Typography>
                </Grid>
            )}

            {commentsResponse?.pageInfo.hasNextPage && (
                <Grid item xs={12}>
                    <AppButton onClick={handleNext} fullWidth color="primary" size="small">
                        Load More
                    </AppButton>
                </Grid>
            )}
        </Grid>
    );
};

export default CommentsBody;
